import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { UsuarioService } from '../../../../../services/usuario.service';
import { User } from '../../../../../models/user.model';
import { allRoles, liquidationTypes } from '../../../../../data/master_lists';
import { isSalesRepRole } from '../../../../../utilities/role-utilities';

@Component({
    selector: 'app-edit-user-modal',
    templateUrl: './edit-user-modal.component.html',
    styleUrls: ['./edit-user-modal.component.scss'],
    standalone: false
})
export class EditUserModalComponent implements OnInit, OnDestroy {
    @Input() user!: User;

    formUpdateUser!: UntypedFormGroup;
    allRolesOptions = allRoles;
    liquidationTypes = liquidationTypes;
    loading = false;
    private roleSub?: Subscription;

    constructor(
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private usuarioService: UsuarioService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        this.initForm();
    }

    private initForm(): void {
        const isSales = isSalesRepRole(this.user?.role);
        this.formUpdateUser = this.fb.group({
            uid: [this.user.uid],
            name: [this.user.name, Validators.required],
            email: [{ value: this.user.email, disabled: true }, [Validators.required, Validators.email]],
            role: [this.user.role, Validators.required],
            // Validador condicional basado en el rol actual del usuario.
            liquidation_type: [(this.user as any).liquidation_type || '', isSales ? [Validators.required] : []],
            // F9: Control de estado activo. Deshabilitado si el usuario a editar es el mismo logueado
            // (prevención de auto-bloqueo en el modal).
            active: [{ value: this.isUserActive(this.user), disabled: this.user?.uid === this.usuarioService.usuario?.uid }]
        });

        // Escucha cambios de rol y actualiza la validación de liquidation_type dinámicamente.
        this.roleSub = this.formUpdateUser.get('role')!.valueChanges.subscribe(role => {
            this.updateLiquidationTypeValidation(role);
        });
    }

    private updateLiquidationTypeValidation(role: string): void {
        const liqControl = this.formUpdateUser.get('liquidation_type');
        if (!liqControl) return;
        if (isSalesRepRole(role)) {
            liqControl.setValidators([Validators.required]);
        } else {
            liqControl.clearValidators();
            liqControl.setValue('');
        }
        liqControl.updateValueAndValidity({ emitEvent: false });
    }

    get isSalesRep(): boolean {
        return isSalesRepRole(this.formUpdateUser?.get('role')?.value);
    }

    // F10: Helper privado — misma lógica que isUserActive() en el componente padre.
    // Mantiene la consistencia sin duplicar el método a través del módulo.
    private isUserActive(user: any): boolean {
        if (!user) return false;
        return user.status === 'active' || user.active === '1' || user.active === 'true' || user.active === (true as any);
    }

    async updateUser(): Promise<void> {
        if (!this.formUpdateUser.valid) {
            this.toastr.warning('All fields are required');
            this.formUpdateUser.markAllAsTouched();
            return;
        }

        this.loading = true;
        try {
            // Need to include email even if disabled
            const formValue = this.formUpdateUser.getRawValue();
            // F11: Mapear boolean active → string '1'/'0' antes de enviar al backend
            formValue.active = formValue.active ? '1' : '0';
            const response = await this.usuarioService.editUserById(formValue);
            this.toastr.success(response?.msg || 'User updated successfully');
            this.activeModal.close(true);
        } catch (error: any) {
            this.toastr.error(error.error?.msg || 'Error updating user');
        } finally {
            this.loading = false;
        }
    }

    closeModal(): void {
        this.activeModal.dismiss();
    }

    ngOnDestroy(): void {
        this.roleSub?.unsubscribe();
    }
}
